import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { getAdminToken } from "./auth.js";

const EMPTY_DRAFT = {
  name: "",
  content: "",
  rating: 5,
  active: true,
};

export default function TestimonialManager({
  testimonials,
  onChanged,
  onUnauthorized,
}) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (editingId && !testimonials.some((item) => item.id === editingId)) {
      setEditingId("");
      setDraft(EMPTY_DRAFT);
    }
  }, [editingId, testimonials]);

  const handleChange = ({ target: { name, value } }) => {
    setDraft((current) => ({
      ...current,
      [name]: name === "rating" ? Number(value) : value,
    }));
    setError("");
    setNotice("");
  };

  const resetForm = () => {
    setEditingId("");
    setDraft(EMPTY_DRAFT);
    setError("");
    setNotice("");
  };

  const editTestimonial = (testimonial) => {
    setEditingId(testimonial.id);
    setDraft({
      name: testimonial.name,
      content: testimonial.content,
      rating: testimonial.rating,
      active: testimonial.active !== false,
    });
    setError("");
    setNotice("");
  };

  const handleRequestError = (requestError, fallback) => {
    if (requestError.status === 401 || requestError.status === 403) {
      onUnauthorized();
      return;
    }
    const validationMessage = requestError.details
      ?.map((detail) => detail.message)
      .join(" ");
    setError(validationMessage || requestError.message || fallback);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (!draft.name.trim() || !draft.content.trim()) {
      setError("Name and content are required.");
      return;
    }

    if (
      !Number.isInteger(draft.rating) ||
      draft.rating < 1 ||
      draft.rating > 5
    ) {
      setError("Rating must be between 1 and 5.");
      return;
    }

    setSubmitting(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        name: draft.name.trim(),
        content: draft.content.trim(),
        rating: draft.rating,
        active: draft.active,
      };

      if (editingId) {
        await api.admin.testimonials.update(
          editingId,
          payload,
          getAdminToken(),
        );
        setNotice("Testimonial updated.");
      } else {
        await api.admin.testimonials.create(payload, getAdminToken());
        setNotice("Testimonial created.");
      }

      setEditingId("");
      setDraft(EMPTY_DRAFT);
      await onChanged();
    } catch (requestError) {
      handleRequestError(requestError, "Could not save the testimonial");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTestimonialVisibility = async (testimonial) => {
    if (submitting || deletingId) return;

    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      await api.admin.testimonials.update(
        testimonial.id,
        { active: testimonial.active === false },
        getAdminToken(),
      );
      setNotice(
        testimonial.active === false
          ? "Testimonial approved."
          : "Testimonial hidden.",
      );
      await onChanged();
    } catch (requestError) {
      handleRequestError(requestError, "Could not update testimonial status");
    } finally {
      setSubmitting(false);
    }
  };

  const removeTestimonial = async (testimonial) => {
    if (deletingId) return;
    const confirmed = window.confirm(
      `Delete the testimonial from ${testimonial.name}? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingId(testimonial.id);
    setError("");
    setNotice("");
    try {
      await api.admin.testimonials.remove(testimonial.id, getAdminToken());
      await onChanged();
      if (editingId === testimonial.id) resetForm();
    } catch (requestError) {
      handleRequestError(requestError, "Could not delete the testimonial");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <section className="admin-panel admin-testimonials-panel">
      <div className="admin-panel-heading">
        <div>
          <p className="admin-eyebrow">Public website</p>
          <h2>Testimonials</h2>
        </div>
      </div>

      <form className="admin-settings-form" onSubmit={handleSubmit}>
        <div className="admin-form-grid">
          <label>
            <span>Full name</span>
            <input
              type="text"
              name="name"
              value={draft.name}
              onChange={handleChange}
              maxLength={100}
              required
            />
          </label>

          <label>
            <span>Star rating</span>
            <select name="rating" value={draft.rating} onChange={handleChange}>
              {[1, 2, 3, 4, 5].map((rating) => (
                <option value={rating} key={rating}>
                  {rating}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          <span>Testimonial content</span>
          <textarea
            name="content"
            value={draft.content}
            onChange={handleChange}
            maxLength={2000}
            required
          />
        </label>

        <label className="admin-checkbox">
          <input
            type="checkbox"
            name="active"
            checked={draft.active}
            onChange={({ target }) =>
              setDraft((current) => ({ ...current, active: target.checked }))
            }
          />
          <span>Display this testimonial publicly</span>
        </label>

        {error && (
          <p className="admin-form-error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="admin-form-notice" role="status">
            {notice}
          </p>
        )}

        <div className="admin-form-actions">
          <button type="submit" disabled={submitting}>
            {submitting
              ? "Saving..."
              : editingId
                ? "Update testimonial"
                : "Create testimonial"}
          </button>
          {editingId && (
            <button
              type="button"
              className="admin-secondary"
              onClick={resetForm}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Content</th>
              <th>Rating</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {testimonials.map((testimonial) => (
              <tr key={testimonial.id}>
                <td>{testimonial.name}</td>
                <td>{testimonial.content}</td>
                <td>{testimonial.rating}</td>
                <td>
                  <span
                    className={`admin-payment-status${
                      testimonial.active === false ? " is-processing" : " is-succeeded"
                    }`}
                  >
                    {testimonial.active === false ? "Pending" : "Visible"}
                  </span>
                </td>
                <td>
                  <div className="admin-form-actions">
                    <button
                      type="button"
                      className="admin-secondary"
                      onClick={() => editTestimonial(testimonial)}
                      disabled={Boolean(deletingId)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-secondary"
                      onClick={() => toggleTestimonialVisibility(testimonial)}
                      disabled={Boolean(deletingId) || submitting}
                    >
                      {testimonial.active === false ? "Approve" : "Hide"}
                    </button>
                    <button
                      type="button"
                      className="admin-delete"
                      onClick={() => removeTestimonial(testimonial)}
                      disabled={Boolean(deletingId)}
                    >
                      {deletingId === testimonial.id ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {testimonials.length === 0 && (
              <tr>
                <td style={{ color: "#667085" }} colSpan="5">
                  No testimonials yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
